/**
 * MatrixCell Component
 * Individual cell in the matrix grid showing qualification status for a team member
 *
 * Features:
 * - Status-based styling (valid, soon, expired, missing)
 * - Cross-space qualification handling
 * - Drag-and-drop support
 * - Click/context menu handlers
 * - Custom memo comparison for performance
 */

import { memo } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  Eye,
  FileText,
  Calendar,
  Clock,
  XCircle,
  Loader2,
  ShieldAlert
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

const MatrixCell = memo(({
  member,
  qualification,
  req,
  isHighlighted,
  isColumnHighlighted,
  dockOpen,
  dockCell,
  getCellDragProps,
  isCellDragOver,
  handleCellClick,
  handleCellRightClick,
  getStatusLabel,
  getStatusDateText,
  setSelectedCell,
  setCrossSpaceModalOpen,
  fullRequirement,
  handleOneClickQuote,
  setDockCell,
  setDockOpen,
  navigate,
  projectId,
  isAssigning
}) => {
  const Icon = req.icon;
  const isCellHighlighted = isHighlighted || isColumnHighlighted;

  // Build border classes
  let borderClasses = '';
  if (isColumnHighlighted) {
    borderClasses += 'border-l-2 border-r-2 ';
  }
  if (isHighlighted) {
    borderClasses += 'border-t-2 border-b-2 ';
  }
  if (borderClasses) {
    borderClasses += 'border-blue-400';
  }

  return (
    <td
      key={req.id}
      className={`border-0 cursor-pointer relative transition-colors ${
        isCellHighlighted ? `bg-blue-50 ${borderClasses}` : ''
      }`}
      style={{ width: '112px', height: '112px', minWidth: '112px', minHeight: '112px', maxWidth: '112px', maxHeight: '112px', padding: '0' }}
    >
      <div className="w-full h-full flex items-center justify-center p-1">
      <div
        className={`
          rounded-xl relative overflow-hidden group
          ${isCellDragOver(member, qualification) ? '' : 'transition-all duration-400 ease-out'}
          ${req.isAssigned ? (
            req.status === 'valid' ?
            'bg-green-200/70 border border-green-300/30 hover:bg-green-200/80 hover:border-green-300/40' :
            req.status === 'soon' ?
            'bg-amber-200/70 border border-amber-300/30 hover:bg-amber-200/80 hover:border-amber-300/40' :
            req.status === 'expired' ?
            'bg-red-200/70 border border-red-300/30 hover:bg-red-200/80 hover:border-red-300/40' :
            req.status === 'missing' ?
            'bg-red-200/70 border border-red-300/30 hover:bg-red-200/80 hover:border-red-300/40' :
            req.status === 'pending' ?
            'bg-gray-200/70 border border-gray-300/30 hover:bg-gray-200/80 hover:border-gray-300/40' :
            'bg-gray-200/70 border border-gray-300/30'
          ) : (req.status === 'valid' || req.status === 'soon') ? (
            // Unassigned but qualified (direct or cross-space) - gray background with colored inner border
            req.status === 'valid' ?
            'bg-gray-200/70 border border-gray-300/30 hover:bg-gray-200/80 hover:border-gray-300/40 shadow-[inset_0_0_0_2px_rgb(134_239_172/0.5)]' :
            'bg-gray-200/70 border border-gray-300/30 hover:bg-gray-200/80 hover:border-gray-300/40 shadow-[inset_0_0_0_2px_rgb(253_224_71/0.5)]'
          ) : (
            'bg-gray-100/70 border border-gray-300/30 hover:bg-gray-200/70 hover:border-gray-300/40'
          )}
          ${dockOpen && dockCell?.member?.id === member.id && dockCell?.requirement?.id === qualification.id ?
            'ring-2 ring-blue-400/50 shadow-xl scale-110 z-50' :
            'hover:shadow-md'}
        `}
        style={isCellDragOver(member, qualification) ? {
          width: '104px',
          height: '104px',
          border: '3px dashed #3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          boxShadow: '0 0 0 4px #3b82f6',
          transform: 'scale(1.05)',
          zIndex: 100
        } : {
          width: '104px',
          height: '104px'
        }}
        onClick={(e) => handleCellClick(member, qualification, req.status, e, req)}
        onContextMenu={(e) => handleCellRightClick(member, qualification, req.status, e)}
        {...getCellDragProps(member, qualification, req.status, req.assignment)}
      >
        {isAssigning && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 z-50 rounded" style={{ pointerEvents: 'none' }}>
            <Loader2 className="h-6 w-6 text-indigo-500 animate-spin" />
          </div>
        )}
        {req.isOverridden && (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute top-0 right-0 z-[5] cursor-pointer" onClick={(e) => e.stopPropagation()}>
                  <div style={{ width: 0, height: 0, borderLeft: '28px solid transparent', borderTop: '28px solid #f97316' }} />
                  <ShieldAlert className="absolute top-[4px] right-[3px] h-2.5 w-2.5 text-white" strokeWidth={2.5} />
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="end" className="max-w-xs bg-white border border-gray-200 shadow-lg p-3" sideOffset={2}>
                <div className="flex items-center gap-1.5 mb-2">
                  <ShieldAlert className="h-3.5 w-3.5 text-orange-500" />
                  <span className="text-xs font-semibold text-gray-900">Manual Override</span>
                </div>
                {req.overrideInfo?.reason && (
                  <div className="mb-2">
                    <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Status</span>
                    <p className="text-xs text-gray-900 mt-0.5 capitalize">{req.overrideInfo.reason.replace(/_/g, ' ')}</p>
                  </div>
                )}
                <div className="flex items-center gap-3 text-[10px] text-gray-500">
                  {req.overrideInfo?.by && (
                    <span>By: <span className="text-gray-700 font-medium">{req.overrideInfo.by}</span></span>
                  )}
                  {req.overrideInfo?.date && (
                    <span>{new Date(req.overrideInfo.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <div className="h-full flex items-center justify-center pointer-events-none group relative z-10">
          <div
            key={`status-${req.status}-${req.isAssigned}`}
            className="flex flex-col items-center justify-center text-center py-2 px-1 h-full relative w-full transition-all duration-200 ease-out"
          >
            {req.status === 'unassigned' ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex flex-col items-center gap-0.5">
                  <div className="border-2 border-dashed border-gray-300 rounded-md p-2 flex items-center justify-center transition-all duration-200 group-hover:border-gray-400 group-hover:scale-75">
                    <Plus className="h-4 w-4 text-gray-400 group-hover:text-gray-500" strokeWidth={2} />
                  </div>
                  <div className="h-0 overflow-hidden group-hover:h-auto transition-all duration-200">
                    <span className="text-[9px] font-medium text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                      Click to assign
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Hover overlay for unassigned but qualified cells (direct or cross-space) */}
                {!req.isAssigned && (req.status === 'valid' || req.status === 'soon') && (
                  <div className="absolute top-2 left-0 right-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                    <div className="flex flex-col items-center gap-1">
                      <div className="border-2 border-dashed border-gray-300 rounded-md p-2 flex items-center justify-center transition-all duration-200">
                        <Plus className="h-4 w-4 text-gray-400" strokeWidth={2} />
                      </div>
                      <span className="text-[9px] font-medium text-gray-500 whitespace-nowrap">
                        Click to assign
                      </span>
                    </div>
                  </div>
                )}

                {/* Helper to determine if content should fade out on hover */}
                {(() => {
                  const shouldFadeOnHover = !req.isAssigned && (req.status === 'valid' || req.status === 'soon');
                  return (
                    <>
                      <Icon className={`h-4 w-4 mb-1 transition-opacity duration-200
                        ${shouldFadeOnHover ? 'group-hover:opacity-0' : ''}
                        ${req.isAssigned ? (
                          req.status === 'valid' ? 'text-green-700' :
                          req.status === 'soon' ? 'text-amber-700' :
                          req.status === 'expired' ? 'text-red-700' :
                          req.status === 'missing' ? 'text-red-700' :
                          req.status === 'pending' ? 'text-gray-700' : 'text-gray-700'
                        ) : (
                          req.status === 'valid' ? 'text-green-500' :
                          req.status === 'soon' ? 'text-amber-500' :
                          req.status === 'expired' ? 'text-red-500' :
                          req.status === 'missing' ? 'text-red-500' :
                          req.status === 'unassigned' ? 'text-gray-500' :
                          req.status === 'pending' ? 'text-gray-500' : 'text-gray-500'
                        )}
                      `} strokeWidth={1.5} />
                      <span className={`text-[11px] font-normal leading-tight transition-opacity duration-200 ${shouldFadeOnHover ? 'group-hover:opacity-0' : ''} ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>
                        {getStatusLabel(req.status)}
                      </span>
                      {(req.status === 'missing' || req.status === 'expired') && (req.renewalStatus === 'quoting' || req.renewalStatus === 'quoted' || req.renewalStatus === 'booked') ? (
                        <span className={`text-[10px] mt-1 leading-tight text-gray-600 transition-opacity duration-200 ${shouldFadeOnHover ? 'group-hover:opacity-0' : ''}`}>
                          {req.renewalStatus === 'quoting' ? 'Quote Requested' :
                           req.renewalStatus === 'quoted' ? 'Quote Provided' :
                           req.renewalStatus === 'booked' ? 'Training Booked' :
                           'Not Compliant'}
                        </span>
                      ) : req.status === 'missing' ? (
                        <span className={`text-[10px] mt-1 leading-tight text-gray-600 transition-opacity duration-200 ${shouldFadeOnHover ? 'group-hover:opacity-0' : ''}`}>
                          Not Compliant
                        </span>
                      ) : req.date ? (
                        <span className={`text-[10px] mt-1 leading-tight text-gray-600 transition-opacity duration-200 ${shouldFadeOnHover ? 'group-hover:opacity-0' : ''}`}>
                          {getStatusDateText(req.status, req.date)}
                        </span>
                      ) : (
                        <span className="text-[10px] text-transparent mt-1 leading-tight select-none">
                          &nbsp;
                        </span>
                      )}
                    </>
                  );
                })()}
                {/* Show Quick Quote badge for missing/expired status (only if no quote exists) */}
                {(req.status === 'missing' || req.status === 'expired') && req.renewalStatus !== 'quoting' && req.renewalStatus !== 'booked' && req.renewalStatus !== 'quoted' ? (
                  <div
                    className={`mt-2 px-2 py-1 bg-white/90 border border-white rounded-full flex items-center gap-1 shadow-sm cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 pointer-events-auto ${req.qualifiedFromOtherSpace ? 'group-hover:opacity-0' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      // Call handleOneClickQuote directly with the cell data
                      handleOneClickQuote({
                        member: member,
                        requirement: fullRequirement,
                        status: req.status,
                        assignment: req.assignment
                      });
                    }}
                  >
                    <Plus className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                    <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>Quick Quote</span>
                  </div>
                ) : (req.status === 'missing' || req.status === 'expired') && req.renewalStatus === 'quoting' ? (
                  <div
                    className="mt-2 px-2 py-1 bg-white/90 border border-white rounded-full flex items-center gap-1 shadow-sm cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 pointer-events-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Navigate to bookings page with the quote
                      const quoteId = req.assignment?.quote_id;
                      if (quoteId) {
                        // Get projectId from the current URL path
                        const pathParts = window.location.pathname.split('/');
                        const spaceIndex = pathParts.indexOf('space');
                        const currentProjectId = spaceIndex >= 0 ? pathParts[spaceIndex + 1] : null;

                        if (currentProjectId) {
                          window.location.href = `/space/${currentProjectId}/bookings/${quoteId}`;
                        } else {
                          toast.error('Unable to navigate to quote');
                        }
                      } else {
                        toast.error('Quote not found');
                      }
                    }}
                  >
                    <FileText className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                    <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>View Quote</span>
                  </div>
                ) : req.status === 'valid' ? (
                  <div
                    className={`mt-2 px-2 py-1 bg-white/90 border border-white rounded-full flex items-center gap-1 shadow-sm cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 pointer-events-auto relative ${!req.isAssigned ? 'group-hover:opacity-0' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      // For unassigned cells, open CrossSpaceModal to assign; otherwise open DetailsModal
                      if (!req.isAssigned) {
                        setSelectedCell({ member, requirement: fullRequirement, status: req.status });
                        setCrossSpaceModalOpen(true);
                      } else {
                        handleCellClick(member, qualification, req.status, e, { ...req, isAssigned: true });
                      }
                    }}
                  >
                    <Eye className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                    <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>View</span>
                  </div>
                ) : req.status === 'soon' ? (
                  <div
                    className={`mt-2 px-2 py-1 bg-white/90 border border-white rounded-full flex items-center gap-1 shadow-sm cursor-pointer hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 pointer-events-auto relative ${!req.isAssigned ? 'group-hover:opacity-0' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      // For unassigned cells, open CrossSpaceModal to assign; otherwise open DetailsModal
                      if (!req.isAssigned) {
                        setSelectedCell({ member, requirement: fullRequirement, status: req.status });
                        setCrossSpaceModalOpen(true);
                      } else {
                        handleCellClick(member, qualification, req.status, e, { ...req, isAssigned: true });
                      }
                    }}
                  >
                    <Eye className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                    <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>View</span>
                  </div>
                ) : req.renewalStatus && req.renewalStatus !== 'n/a' && (
                  <div
                    className="mt-2 px-2 py-1 bg-white/90 border border-white rounded-full flex items-center gap-1 shadow-sm transition-opacity duration-200 pointer-events-auto relative cursor-pointer hover:bg-gray-50 hover:border-gray-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCellClick(member, req, req.status, e);
                    }}
                  >
                    {/* Original badge content - fades out on hover for unassigned-but-qualified cells */}
                    <div className={`flex items-center gap-1 transition-opacity duration-200 ${!req.isAssigned && (req.status === 'valid' || req.status === 'soon') ? 'group-hover:opacity-0' : ''}`}>
                      {req.renewalStatus === 'quoting' ? (
                        <>
                          <FileText className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                          <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>Quoting</span>
                        </>
                      ) : req.renewalStatus === 'quoted' ? (
                        <>
                          <FileText className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                          <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>Quoted</span>
                        </>
                      ) : req.renewalStatus === 'booked' ? (
                        <>
                          <Calendar className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                          <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>Booked</span>
                        </>
                      ) : req.renewalStatus === 'pending' ? (
                        <>
                          <Clock className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                          <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>Pending</span>
                        </>
                      ) : req.renewalStatus === 'failed' ? (
                        <>
                          <XCircle className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                          <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>Failed</span>
                        </>
                      ) : null}
                    </div>
                    {/* "View" overlay - fades in on hover for unassigned-but-qualified cells */}
                    {!req.isAssigned && (req.status === 'valid' || req.status === 'soon') && (
                      <div
                        className="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer z-10"
                        onClick={(e) => {
                          e.stopPropagation();
                          // For cross-space unassigned, open CrossSpaceModal
                          if (!req.isAssigned) {
                            setSelectedCell({ member, requirement: fullRequirement, status: req.status });
                            setCrossSpaceModalOpen(true);
                          } else {
                            handleCellClick(member, req, req.status, e, { ...req, isAssigned: true });
                          }
                        }}
                      >
                        <Eye className={`h-3 w-3 ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`} strokeWidth={1.5} />
                        <span className={`text-[9px] font-medium ${req.isAssigned ? 'text-gray-800' : 'text-gray-700'}`}>View</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </td>
  );
}, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.req.status === nextProps.req.status &&
    prevProps.req.isAssigned === nextProps.req.isAssigned &&
    prevProps.req.date === nextProps.req.date &&
    prevProps.req.renewalStatus === nextProps.req.renewalStatus &&
    prevProps.isHighlighted === nextProps.isHighlighted &&
    prevProps.isColumnHighlighted === nextProps.isColumnHighlighted &&
    prevProps.dockOpen === nextProps.dockOpen &&
    prevProps.dockCell?.member?.id === nextProps.dockCell?.member?.id &&
    prevProps.dockCell?.requirement?.id === nextProps.dockCell?.requirement?.id &&
    prevProps.isAssigning === nextProps.isAssigning &&
    prevProps.req.isOverridden === nextProps.req.isOverridden
  );
});

MatrixCell.displayName = 'MatrixCell';

export default MatrixCell;
